import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LogOut, Play, Coins, TrendingUp, TrendingDown, X, Gift, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { Navbar } from '@/components/Navbar';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, updateChips, signOut } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [initialChips, setInitialChips] = useState(0);
  const [totalGames, setTotalGames] = useState(5);
  const [customChips, setCustomChips] = useState('');
  const [canClaimReward, setCanClaimReward] = useState(false);
  const [claimingReward, setClaimingReward] = useState(false);
  const [timeUntilNextReward, setTimeUntilNextReward] = useState('');
  const [showRewardCard, setShowRewardCard] = useState(false); // Inicialitzar a false, es mostrarà després de comprovar
  const [isHidingReward, setIsHidingReward] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    // Refresh chips when component mounts
    if (user) {
      setIsRefreshing(true);
      updateChips().finally(() => {
        setIsRefreshing(false);
      });
      checkDailyReward();
    }
    
    // Carregar configuració guardada
    const config = localStorage.getItem('practica_config');
    if (config) {
      try {
        const { initialChips: savedChips } = JSON.parse(config);
        if (savedChips !== undefined) {
          setInitialChips(savedChips);
          // Si el valor guardat no és una opció predefinida, mostrar-lo a l'input personalitzat
          if (![0, 200, 500].includes(savedChips)) {
            setCustomChips(savedChips.toString());
          }
        }
      } catch (error) {
        console.error('Error loading config:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Comprovar si es pot reclamar la recompensa diària
  const checkDailyReward = async () => {
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('id, last_daily_reward')
      .eq('username', user.username)
      .single();

    if (!profile) return;

    const lastClaim = profile.last_daily_reward ? new Date(profile.last_daily_reward) : null;
    const now = new Date();
    
    // Calcular les 8am d'avui a Espanya (UTC+1 o UTC+2 segons estiu/hivern)
    const madridTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
    const today8am = new Date(madridTime);
    today8am.setHours(8, 0, 0, 0);
    
    // Si estem abans de les 8am, usar les 8am d'ahir com a referència
    if (madridTime.getHours() < 8) {
      today8am.setDate(today8am.getDate() - 1);
    }

    // Pot reclamar si mai ha reclamat o si l'última reclaimació va ser abans de les 8am d'avui
    const canClaim = !lastClaim || lastClaim < today8am;
    setCanClaimReward(canClaim);
    setShowRewardCard(canClaim); // Mostrar la targeta només si es pot reclamar

    // Calcular temps fins a la següent recompensa
    if (!canClaim && lastClaim) {
      const next8am = new Date(today8am);
      next8am.setDate(next8am.getDate() + 1);
      updateTimeUntilReward(next8am);
    }
  };

  // Actualitzar el temps fins a la següent recompensa
  const updateTimeUntilReward = (next8am: Date) => {
    const updateTimer = () => {
      const now = new Date();
      const diff = next8am.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCanClaimReward(true);
        setShowRewardCard(true);
        setTimeUntilNextReward('');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeUntilNextReward(`${hours}h ${minutes}m`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Actualitzar cada minut
    return () => clearInterval(interval);
  };

  // Reclamar la recompensa diària
  const claimDailyReward = async () => {
    if (!user || !canClaimReward || claimingReward) return;

    setClaimingReward(true);

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, chips')
        .eq('username', user.username)
        .single();

      if (!profile) {
        toast.error('Error al carregar el perfil');
        return;
      }

      const rewardAmount = 100;
      const { error } = await supabase
        .from('profiles')
        .update({
          chips: profile.chips + rewardAmount,
          last_daily_reward: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (error) {
        toast.error('Error al reclamar la recompensa');
        return;
      }

      await updateChips(profile.chips + rewardAmount);
      setCanClaimReward(false);
      toast.success(`🎁 +${rewardAmount} fitxes! Recompensa diària reclamada`);
      
      // Amagar la targeta després de 5 segons amb animació
      setTimeout(() => {
        setIsHidingReward(true);
        setTimeout(() => {
          setShowRewardCard(false);
        }, 300); // Temps de l'animació
      }, 5000); // 5 segons
      
      // Calcular la següent recompensa
      const now = new Date();
      const madridTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
      const next8am = new Date(madridTime);
      next8am.setDate(next8am.getDate() + 1);
      next8am.setHours(8, 0, 0, 0);
      updateTimeUntilReward(next8am);
    } catch (error) {
      console.error('Error claiming reward:', error);
      toast.error('Error al reclamar la recompensa');
    } finally {
      setClaimingReward(false);
    }
  };

  const handlePlayPractica = () => {
    setShowConfigModal(true);
  };

  const handleChipsChange = (value: number) => {
    const allowedValues = [0, 200, 500];
    if (allowedValues.includes(value)) {
      // Si és 0, sempre està disponible (mode gratuït)
      if (value === 0) {
        setInitialChips(0);
        setCustomChips(''); // Netejar input personalitzat
      } else {
        const maxChips = user ? user.chips : 10000;
        const finalValue = Math.min(value, maxChips);
        setInitialChips(finalValue);
        setCustomChips(''); // Netejar input personalitzat quan es selecciona una opció predefinida
      }
    }
  };

  const handleCustomChipsChange = (value: string) => {
    setCustomChips(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      // Si el valor personalitzat no és una de les opcions predefinides, establir-lo
      if (![0, 200, 500].includes(numValue)) {
        const maxChips = user ? user.chips : 10000;
        const finalValue = Math.min(numValue, maxChips);
        setInitialChips(finalValue);
      } else {
        // Si coincideix amb una opció predefinida, netejar l'input i usar l'opció predefinida
        setCustomChips('');
      }
    } else if (value === '') {
      // Si l'input està buit, no canviar initialChips (mantenir l'últim valor vàlid)
    }
  };

  const handleStartGame = () => {
    // Si hi ha un valor personalitzat que no coincideix amb les opcions predefinides, usar-lo
    const finalChips = customChips && !isNaN(parseInt(customChips, 10)) && ![0, 200, 500].includes(parseInt(customChips, 10))
      ? parseInt(customChips, 10)
      : initialChips;
    
    // Guardar configuració a localStorage
    localStorage.setItem('practica_config', JSON.stringify({
      initialChips: finalChips,
      totalGames
    }));
    setShowConfigModal(false);
    navigate('/practica');
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
  };

  if (!user) {
    return null;
  }

  const defaultInitialChips = 1000;
  const chipsChange = user.chips - defaultInitialChips;
  const chipsChangePercent = ((chipsChange / defaultInitialChips) * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-start p-4 sm:p-6 relative overflow-y-auto">
      <Navbar />
      {/* Enhanced gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-emerald-900/30 via-transparent to-transparent blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-red-900/30 via-transparent to-transparent blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-900/10 to-purple-900/10 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md space-y-6 relative z-10 py-8 mx-auto pt-20 pb-24">
        {/* Fitxes totals */}
        <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl overflow-hidden relative">
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          
          <CardContent className="pt-6 relative z-10">
            <div className="space-y-4">
              {/* Header with icon */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-400 mb-2 uppercase tracking-wider">Fitxes totals</p>
                  <div className="flex items-baseline gap-3">
                    <p className="text-4xl font-black text-white tabular-nums">
                      {user.chips.toLocaleString()}
                    </p>
                    <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <Coins className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Change indicator below */}
              {chipsChange !== 0 && (
                <div className="pt-3 border-t border-zinc-800 space-y-2">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg w-fit ${
                    chipsChange >= 0 
                      ? 'bg-emerald-500/10 border border-emerald-500/20' 
                      : 'bg-red-500/10 border border-red-500/20'
                  }`}>
                    {chipsChange >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-400 shrink-0" />
                    )}
                    <p className={`text-sm font-semibold tabular-nums whitespace-nowrap ${
                      chipsChange >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {chipsChange >= 0 ? '+' : ''}{chipsChange.toLocaleString()} ({chipsChangePercent}%)
                    </p>
                  </div>
                  <p className="text-xs text-zinc-500">
                    Respecte a les fitxes inicials
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recompensa Diària */}
        {showRewardCard && (
          <Card className={cn(
            "border-zinc-800 bg-gradient-to-br from-amber-900/20 to-orange-900/20 backdrop-blur-xl relative overflow-hidden transition-all duration-300",
            isHidingReward && "opacity-0 scale-95 -translate-y-2"
          )}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            <CardContent className="pt-6 relative z-10">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
                      <Gift className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Recompensa Diària</h3>
                      <p className="text-xs text-zinc-400">+100 fitxes cada dia</p>
                    </div>
                  </div>
                </div>

                {canClaimReward ? (
                  <Button
                    onClick={claimDailyReward}
                    disabled={claimingReward}
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-base shadow-lg shadow-amber-500/20"
                  >
                    {claimingReward ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Reclamant...
                      </>
                    ) : (
                      <>
                        <Gift className="w-5 h-5 mr-2" />
                        Reclamar recompensa
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-zinc-400" />
                        <p className="text-sm text-zinc-400">Pròxima recompensa:</p>
                      </div>
                      <p className="text-sm font-bold text-amber-400">{timeUntilNextReward || 'Aviat'}</p>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Les recompenses es renoven cada dia a les 8:00 AM</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mode de joc */}
        <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">
                  🎯 Pràctica
                </h2>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>• Partides contra 3 bots</li>
                  <li>• Tria entre 3, 5 o 10 rondes</li>
                </ul>
              </div>

              <Button
                onClick={handlePlayPractica}
                className="w-full bg-white hover:bg-zinc-100 text-black font-bold h-12 text-base"
              >
                <Play className="w-5 h-5 mr-2" />
                Començar partida
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mode Online */}
        <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">
                  🌐 Online
                </h2>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>• Partides en temps real</li>
                  <li>• 2 jugadors reals + 2 bots</li>
                </ul>
              </div>

              <Button
                onClick={() => navigate('/online')}
                className="w-full bg-white hover:bg-zinc-100 text-black font-bold h-12 text-base"
              >
                <Play className="w-5 h-5 mr-2" />
                Jugar online
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tancar sessió */}
        <Button
          onClick={async () => {
            await signOut();
            navigate('/');
          }}
          variant="outline"
          className="w-full border-zinc-700 hover:bg-zinc-800 text-zinc-300 hover:text-white font-semibold h-12 text-base"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Tancar sessió
        </Button>
      </div>

      {/* Modal de configuració */}
      {showConfigModal && (
        <>
          {/* Overlay - cobreix tot incloent el que està sota la navbar */}
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[60]"
            onClick={handleCloseModal}
            style={{ backdropFilter: 'blur(12px)' }}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl relative pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-1">Configuració de la partida</h2>
                    <p className="text-sm text-zinc-400">Personalitza la teva experiència de joc</p>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors flex-shrink-0 ml-4"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Fitxes inicials */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-3">
                        Fitxes inicials
                      </label>
                      <div className="grid grid-cols-3 gap-2.5">
                        {[0, 200, 500].map((chips) => {
                          const hasEnoughChips = chips === 0 || (user ? user.chips >= chips : true);
                          const isSelected = initialChips === chips;
                          return (
                            <button
                              key={chips}
                              onClick={() => hasEnoughChips && handleChipsChange(chips)}
                              disabled={!hasEnoughChips}
                              className={cn(
                                "px-4 py-3.5 rounded-lg font-semibold text-sm transition-all",
                                isSelected
                                  ? "bg-white text-black shadow-lg scale-[1.02]"
                                  : hasEnoughChips
                                  ? "bg-zinc-800 text-white hover:bg-zinc-700 hover:scale-[1.01]"
                                  : "bg-zinc-800/50 text-zinc-600 cursor-not-allowed opacity-50"
                              )}
                            >
                              {chips === 0 ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <span>Gratuït</span>
                                  <span className="text-[10px] font-normal opacity-75">200 virtuals</span>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center gap-0.5">
                                  <span>{chips}</span>
                                  <span className="text-[10px] font-normal opacity-75">fitxes reals</span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Input personalitzat */}
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-zinc-400 mb-2">
                          O introdueix un valor personalitzat
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={user?.chips || 10000}
                          value={customChips}
                          onChange={(e) => handleCustomChipsChange(e.target.value)}
                          onBlur={(e) => {
                            const numValue = parseInt(e.target.value, 10);
                            if (isNaN(numValue) || numValue <= 0) {
                              setCustomChips('');
                            } else if ([0, 200, 500].includes(numValue)) {
                              // Si coincideix amb una opció predefinida, netejar l'input
                              setCustomChips('');
                            }
                          }}
                          placeholder="Ex: 1000"
                          className={cn(
                            "w-full px-4 py-2.5 rounded-lg bg-zinc-800 border text-white text-sm font-medium transition-all",
                            "focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent",
                            "placeholder:text-zinc-500",
                            customChips && !isNaN(parseInt(customChips, 10)) && parseInt(customChips, 10) > 0 && ![0, 200, 500].includes(parseInt(customChips, 10))
                              ? "border-white"
                              : "border-zinc-700"
                          )}
                        />
                        {customChips && !isNaN(parseInt(customChips, 10)) && parseInt(customChips, 10) > 0 && ![0, 200, 500].includes(parseInt(customChips, 10)) && (
                          <p className="mt-1.5 text-xs text-zinc-400">
                            Començaràs amb <span className="font-semibold text-white">{parseInt(customChips, 10).toLocaleString()}</span> fitxes reals
                          </p>
                        )}
                        {initialChips > 0 && ![0, 200, 500].includes(initialChips) && !customChips && (
                          <p className="mt-1.5 text-xs text-zinc-400">
                            Valor personalitzat: <span className="font-semibold text-white">{initialChips.toLocaleString()}</span> fitxes reals
                          </p>
                        )}
                      </div>
                      
                      {/* Informació de fitxes disponibles */}
                      {initialChips === 0 ? (
                        <div className="mt-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                          <p className="text-xs text-zinc-400 leading-relaxed">
                            <span className="font-semibold text-zinc-300">Mode gratuït:</span> Jugues amb 200 fitxes virtuals.{' '}
                            <span className="sm:inline block">No gastes ni guanyes fitxes reals.</span>
                          </p>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className="text-zinc-500">Fitxes disponibles:</span>
                          <span className="font-semibold text-zinc-300">{user?.chips.toLocaleString() || 0}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Separador */}
                  <div className="h-px bg-zinc-800" />
                  
                  {/* Nombre de rondes */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-zinc-300 mb-3">
                        Nombre de rondes
                      </label>
                      <div className="grid grid-cols-3 gap-2.5">
                        {[3, 5, 10].map((rounds) => {
                          const isSelected = totalGames === rounds;
                          return (
                            <button
                              key={rounds}
                              onClick={() => setTotalGames(rounds)}
                              className={cn(
                                "px-4 py-3.5 rounded-lg font-semibold text-sm transition-all",
                                isSelected
                                  ? "bg-white text-black shadow-lg scale-[1.02]"
                                  : "bg-zinc-800 text-white hover:bg-zinc-700 hover:scale-[1.01]"
                              )}
                            >
                              {rounds} rondes
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Validació de fitxes */}
                  {user && initialChips > 0 && user.chips < initialChips && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-red-500/20 flex-shrink-0">
                          <Coins className="w-4 h-4 text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-red-400 mb-1.5">
                            Fitxes insuficients
                          </p>
                          <p className="text-xs text-zinc-400 leading-relaxed">
                            Tens <span className="font-semibold text-zinc-300">{user.chips.toLocaleString()}</span> fitxes disponibles, però necessites <span className="font-semibold text-zinc-300">{initialChips.toLocaleString()}</span> fitxes per començar aquesta partida.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Botó d'acció */}
                <div className="mt-8 pt-6 border-t border-zinc-800">
                  <Button
                    onClick={handleStartGame}
                    disabled={user && initialChips > 0 ? user.chips < initialChips : false}
                    className={cn(
                      "w-full font-bold h-12 text-base transition-all",
                      user && initialChips > 0 && user.chips < initialChips
                        ? "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                        : "bg-white hover:bg-zinc-100 text-black hover:scale-[1.02] active:scale-[0.98]"
                    )}
                  >
                    <Play className="w-5 h-5 mr-2" />
                    {user && initialChips > 0 && user.chips < initialChips 
                      ? "Fitxes insuficients" 
                      : "Començar partida"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

